/* eslint-disable no-underscore-dangle */
import Qs from 'qs';

import { APIAdapter } from '@/lib/http';
import accessTokenService from 'utils/accessTokenService';
import {
  createAccessTokenRefresher,
  KINETIK_ACCESS_TOKEN_ENDPOINT,
} from 'utils/createAccessTokenRefresher';

const kinetikApi = APIAdapter(process.env.KINETIK_SERVICES_API_URL, {
  paramsSerializer: (params) => Qs.stringify(params, { arrayFormat: 'repeat' }),
});

// TODO: Consider using jwt-decode package
const parseJwt = (token) => {
  if (!token) return {};

  return JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
};

const refreshAccessToken = createAccessTokenRefresher(kinetikApi);

// Request interceptor to add Authorization header to every request
kinetikApi.interceptors.request.use(
  async (config) => {
    const newConfig = config;

    // Determine version of API to use based on service name specified in config
    if (config.serviceName) {
      newConfig.baseURL = `${process.env.KINETIK_SERVICES_API_URL}.1`;
    }

    // Only add the Authorization header to requests that are not for a new access token
    if (config.url !== KINETIK_ACCESS_TOKEN_ENDPOINT) {
      let accessToken = accessTokenService.getAccessToken();

      // If the access token is missing or is expired, refresh it
      const { exp } = parseJwt(accessToken);
      if (!accessToken || Date.now() >= exp * 1000) {
        accessToken = await refreshAccessToken();
      }

      newConfig.headers.Authorization = `Bearer ${accessToken}`;
    }

    return newConfig;
  },
  (error) => {
    Promise.reject(error);
  },
);

// Response interceptor to retry unauthorized requests after refreshing the access token
kinetikApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If the request is NOT for a new access token,
    //  and the error is 401 (Unauthorized),
    //  and this is not a retry,
    // then refresh the access token and retry the request
    if (
      originalRequest.url !== KINETIK_ACCESS_TOKEN_ENDPOINT &&
      error.response?.status === 401 &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      // Attempt to refresh the access token
      const newAccessToken = await refreshAccessToken();

      // Update the original request with the new access token
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

      // Retry the original request
      return kinetikApi(originalRequest);
    }

    // An unhandled error occurred
    return Promise.reject(error);
  },
);

export default kinetikApi;
